import { closest } from '@nrk/dh-basis-common'
import altStyles from './content/cards/alt-text.module.css'

/**
 * Scroll directly to position. Used for scrolling to the correct position when
 * screen reader sets focus.
 *
 * Used due to the scrollIntoView bug with screen reader:
 *
 * https://bugs.webkit.org/show_bug.cgi?id=245722
 * TODO: remove once bugs are fixed.
 *
 * @param {number} scrollTop
 * @package
 */
function screenReaderScrollTo(scrollTop) {
  // eslint-disable-next-line compat/compat
  const scrollingElement = document.scrollingElement
  scrollingElement.scrollTop = scrollTop
}

/**
 * Disable scroll-snap when navigated with screen reader due to the following Safari bugs:
 * https://bugs.webkit.org/show_bug.cgi?id=250999
 * https://bugs.webkit.org/show_bug.cgi?id=251003
 *
 * TODO: remove once bugs are fixed.
 * @package
 */
function disableScrollSnapForScreenReader() {
  const scrollingElement = document.scrollingElement
  scrollingElement.style.setProperty('scroll-snap-type', 'none')
}

export function setupScreenreaderNavigator(app) {
  let pointerDown = false
  let keyDown = false
  let pageshow = false
  let madeVisible = false
  let screenReaderDetected = false

  function handlePointerDown() {
    pointerDown = true
  }

  function handlePointerUp() {
    setTimeout(() => {
      pointerDown = false
    }, 100)
  }

  function handlePointerCancel() {
    setTimeout(() => {
      pointerDown = false
    }, 100)
  }

  function handleKeyDown() {
    keyDown = true
  }

  function handleKeyUp() {
    setTimeout(() => {
      keyDown = false
    }, 100)
  }

  function handlePageshow() {
    pageshow = true
    setTimeout(() => {
      pageshow = false
    }, 1500)
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      madeVisible = true
      setTimeout(() => {
        madeVisible = false
      }, 1500)
    }
  }

  function handleFocus(e) {
    setTimeout(() => deferredHandleFocus(e), 10)
  }

  function deferredHandleFocus(e) {
    // Some elements are used to detect a screen reader when they are focused. This includes all elements with
    // tabindex=-1 and elements with a data-screen-reader-check attribute
    const isScreenreaderTarget =
      (e.target.getAttribute('tabindex') === '-1' &&
        !e.target.hasAttribute('data-sr-fix-ignore-focus')) ||
      e.target.hasAttribute('data-screen-reader-check')
    const potentialScreenreader =
      isScreenreaderTarget && !keyDown && !pointerDown && !pageshow && !madeVisible
    if (!potentialScreenreader) {
      return
    }

    handleScreenReader()
    const backgroundElement = closest(e.target, '[data-background-id]')
    const altTextElement = e.target && closest(e.target, `.${altStyles.altText}`)
    const cardElement =
      e.target.hasAttribute('tabindex') && closest(e.target, '[data-show-background]')
    let triggerElement
    let forceScroll = false
    if (backgroundElement) {
      const backgroundId = backgroundElement.getAttribute('data-background-id')
      if (app.activeBackgroundId !== backgroundId) {
        triggerElement = app.triggerElements.find(
          (elm) => elm.getAttribute('data-show-background') === backgroundId,
        )
      }
    } else if (altTextElement) {
      triggerElement = altTextElement.nextElementSibling
    } else if (cardElement) {
      triggerElement = cardElement
      // Force scroll on cards to make sure they snap to the correct position
      forceScroll = true
    }
    if (triggerElement && (forceScroll || triggerElement.getAttribute('data-state') !== 'ACTIVE')) {
      /* Safari 16 fails on smooth scroll into view */
      // triggerElement.scrollIntoView({ behavior: 'smooth' })
      // TODO: replace with scrollIntoView once bugs are fixed.
      const targetScrollTop =
        document.scrollingElement.scrollTop + triggerElement.getBoundingClientRect().top
      disableScrollSnapForScreenReader()
      screenReaderScrollTo(targetScrollTop)
    }
  }

  /**
   * We disable scroll snap due to voice over + scroll-snap bugs.
   * To "snap" texts to the correct place we add tabindex=-1 to text elements as well
   * TODO: remove when voice over scroll-snap bugs are fixed
   * @package
   */
  function handleScreenReader() {
    if (!screenReaderDetected) {
      screenReaderDetected = true

      // Add extra focus elements for the screen reader
      const textElements = app.querySelectorAll('p,a,h1,h2,li,blockquote, blockquote footer')
      for (const textElement of textElements) {
        // textElement.setAttribute('role', 'text')
        for (const node of textElement.childNodes) {
          if (node.nodeType === Node.TEXT_NODE) {
            let span = document.createElement('span')
            span.textContent = node.textContent
            span.setAttribute('role', 'text')
            span.setAttribute('tabindex', '-1')
            textElement.insertBefore(span, node)
            node.remove()
          }
        }
      }
    }
  }

  app.addEventListener('pointerdown', handlePointerDown)
  app.addEventListener('pointerup', handlePointerUp)
  app.addEventListener('pointercancel', handlePointerCancel)
  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('keyup', handleKeyUp)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pageshow', handlePageshow)

  app.addEventListener('focus', handleFocus, true)

  return () => {
    app.removeEventListener('pointerdown', handlePointerDown)
    app.removeEventListener('pointerup', handlePointerUp)
    app.removeEventListener('pointercancel', handlePointerCancel)
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('keyup', handleKeyUp)
    window.removeEventListener('pageshow', handlePageshow)
    app.removeEventListener('focus', handleFocus, true)
  }
}
